import { Capacitor } from '@capacitor/core'

type Pixel = ((...args: unknown[]) => void) & { q?: unknown[][] }
type MeasurementWindow = Window & {
  oaiq?: Pixel
  resactlyAdsConsent?: boolean
}

const SDK_ID = 'resactly-openai-pixel'
const HOSTS = ['resactly.com', 'www.resactly.com']
let initialized = false

function enabled(): boolean {
  return typeof window !== 'undefined' && !Capacitor.isNativePlatform() &&
    HOSTS.includes(window.location.hostname) &&
    process.env.REACT_APP_OPENAI_ADS_ENABLED === 'true' &&
    !!process.env.REACT_APP_OPENAI_PIXEL_ID &&
    !(navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl &&
    new URLSearchParams(window.location.search).get('debug_analytics') !== '1' &&
    localStorage.getItem('DEBUG_ANALYTICS') !== 'true'
}

// Fail closed until the existing CMP explicitly grants measurement AND marketing.
// Events blocked before consent are dropped, never replayed after a later grant.
export function syncOpenAIConsent(): void {
  try {
    if (!enabled()) return
    const w = window as MeasurementWindow
    const allowed = w.resactlyAdsConsent === true
    if (!initialized && allowed) {
      if (!w.oaiq) {
        const queue: Pixel = (...args: unknown[]) => {
          if (!queue.q) queue.q = []
          queue.q.push(args)
        }
        queue.q = []
        w.oaiq = queue
      }
      w.oaiq('consent', false)
      w.oaiq('init', { pixelId: process.env.REACT_APP_OPENAI_PIXEL_ID })
      initialized = true
      if (!document.getElementById(SDK_ID)) {
        const script = document.createElement('script')
        script.id = SDK_ID
        script.async = true
        script.src = 'https://bzrcdn.openai.com/sdk/oaiq.min.js'
        document.head.appendChild(script)
      }
    }
    if (initialized) w.oaiq?.('consent', allowed)
  } catch { /* Optional advertising measurement must not affect product behavior. */ }
}

export function initializeOpenAIMeasurement(): () => void {
  window.addEventListener('resactly-ads-consent', syncOpenAIConsent)
  syncOpenAIConsent()
  return () => window.removeEventListener('resactly-ads-consent', syncOpenAIConsent)
}

export function prepareOpenAIEventId(name: string, decisionId?: string): string | undefined {
  try {
    if (!enabled()) return undefined
    if (decisionId) return decisionId
    if (!['account_created', 'session_created', 'session_restaurant_added', 'search_result_selected'].includes(name)) return undefined
    return crypto.randomUUID()
  } catch { return undefined }
}

export function emitOpenAIEvent(name: string, eventId: string, isDecision: boolean): void {
  try {
    if (!enabled() || (window as MeasurementWindow).resactlyAdsConsent !== true) return
    syncOpenAIConsent()
    const pixel = (window as MeasurementWindow).oaiq
    if (!initialized || !pixel) return
    // Send no restaurant identity, destination, user identity, search text or monetary value.
    if (name === 'account_created') {
      pixel('measure', 'registration_completed', { type: 'customer_action' }, { event_id: eventId, opt_out: true })
    } else {
      pixel('measure', 'custom', { type: 'custom' }, {
        event_id: eventId, custom_event_name: isDecision ? 'restaurant_decision' : name, opt_out: true,
      })
    }
  } catch { /* First-party and GA4 delivery are independent. */ }
}
